import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, Upload, Image as ImageIcon, Calendar, ChevronUp, ChevronDown, Presentation, Tag, User, GripVertical, Layers, Link2, Copy, Check, BarChart2 } from 'lucide-react';
import { databaseService, storageService, ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const PRESENTATIONS_COLLECTION = 'presentations';
const PRESENTATIONS_BUCKET = 'presentations';

const emptyPresentation = {
  title: '',
  description: '',
  category: '',
  author: '',
  presentationDate: '',
  slideImages: '', // JSON string array of slide image URLs
  slideImageIds: '', // JSON string array of file IDs
  thumbnailUrl: '',
  thumbnailId: '',
  featured: true,
  order: 0,
  slug: '', // URL-friendly identifier for sharing
};

// Generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 50); // Limit length
};

export default function PresentationManagement() {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyPresentation);
  const [uploadingSlides, setUploadingSlides] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [slideImages, setSlideImages] = useState([]); // Array of {url, id}
  const [linkCopied, setLinkCopied] = useState(false);
  const [copiedCardId, setCopiedCardId] = useState(null); // Track which card's link was copied
  const { showToast } = useToast();

  useEffect(() => {
    loadPresentations();
  }, []);

  const loadPresentations = async () => {
    try {
      const result = await databaseService.listDocuments(PRESENTATIONS_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => (a.order || 0) - (b.order || 0));
        setPresentations(sorted);
      }
    } catch (error) {
      console.error('Error loading presentations:', error);
      showToast('Failed to load presentations');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (presentation = null) => {
    if (presentation) {
      setEditingPresentation(presentation);
      setFormData({
        ...emptyPresentation,
        ...presentation,
      });
      // Parse existing slide images
      try {
        const urls = presentation.slideImages ? JSON.parse(presentation.slideImages) : [];
        const ids = presentation.slideImageIds ? JSON.parse(presentation.slideImageIds) : [];
        setSlideImages(urls.map((url, i) => ({ url, id: ids[i] || '' })));
      } catch {
        setSlideImages([]);
      }
    } else {
      setEditingPresentation(null);
      setFormData({ ...emptyPresentation, order: presentations.length });
      setSlideImages([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPresentation(null);
    setFormData(emptyPresentation);
    setSlideImages([]);
  };

  const handleSlidesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate image types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      showToast('Please upload only image files (JPG, PNG, WebP, GIF)');
      return;
    }

    setUploadingSlides(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const fileId = ID.unique();
        const uploadResult = await storageService.uploadFile(PRESENTATIONS_BUCKET, fileId, file);
        
        if (uploadResult.success) {
          const fileUrl = storageService.getFileView(PRESENTATIONS_BUCKET, uploadResult.data.$id);
          return { url: fileUrl, id: uploadResult.data.$id };
        }
        throw new Error(uploadResult.error);
      });

      const newSlides = await Promise.all(uploadPromises);
      setSlideImages(prev => [...prev, ...newSlides]);
      showToast(`${files.length} slide(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading slides:', error);
      showToast('Failed to upload some slides');
    } finally {
      setUploadingSlides(false);
    }
  };

  const removeSlide = async (index) => {
    const slide = slideImages[index];
    try {
      if (slide.id) {
        await storageService.deleteFile(PRESENTATIONS_BUCKET, slide.id);
      }
      setSlideImages(prev => prev.filter((_, i) => i !== index));
      showToast('Slide removed');
    } catch (error) {
      console.error('Error removing slide:', error);
      // Still remove from UI
      setSlideImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveSlide = (index, direction) => {
    const newSlides = [...slideImages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    setSlideImages(newSlides);
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const fileId = ID.unique();
      const uploadResult = await storageService.uploadFile(PRESENTATIONS_BUCKET, fileId, file);
      
      if (uploadResult.success) {
        const thumbnailUrl = storageService.getFileView(PRESENTATIONS_BUCKET, uploadResult.data.$id);
        setFormData(prev => ({
          ...prev,
          thumbnailUrl,
          thumbnailId: uploadResult.data.$id,
        }));
        showToast('Thumbnail uploaded successfully');
      } else {
        throw new Error(uploadResult.error);
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      showToast('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('Presentation title is required');
      return;
    }
    if (slideImages.length === 0) {
      showToast('Please upload at least one slide image');
      return;
    }

    setSaving(true);
    try {
      const presentationData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category.trim(),
        author: formData.author.trim(),
        presentationDate: formData.presentationDate || null,
        slideImages: JSON.stringify(slideImages.map(s => s.url)),
        slideImageIds: JSON.stringify(slideImages.map(s => s.id)),
        thumbnailUrl: formData.thumbnailUrl || slideImages[0]?.url || '',
        thumbnailId: formData.thumbnailId || '',
        featured: formData.featured,
        order: formData.order,
        slug: formData.slug.trim() || null,
      };

      if (editingPresentation) {
        const result = await databaseService.updateDocument(
          PRESENTATIONS_COLLECTION,
          editingPresentation.$id,
          presentationData
        );
        if (result.success) {
          showToast('Presentation updated successfully');
          loadPresentations();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      } else {
        const result = await databaseService.createDocument(
          PRESENTATIONS_COLLECTION,
          presentationData
        );
        if (result.success) {
          showToast('Presentation created successfully');
          loadPresentations();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Error saving presentation:', error);
      showToast('Failed to save presentation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (presentation) => {
    if (!window.confirm(`Delete "${presentation.title}"? This cannot be undone.`)) return;

    try {
      // Delete slide images from storage
      if (presentation.slideImageIds) {
        const ids = JSON.parse(presentation.slideImageIds);
        await Promise.all(ids.map(id => storageService.deleteFile(PRESENTATIONS_BUCKET, id).catch(() => {})));
      }
      if (presentation.thumbnailId) {
        await storageService.deleteFile(PRESENTATIONS_BUCKET, presentation.thumbnailId);
      }

      const result = await databaseService.deleteDocument(PRESENTATIONS_COLLECTION, presentation.$id);
      if (result.success) {
        showToast('Presentation deleted');
        loadPresentations();
      }
    } catch (error) {
      console.error('Error deleting presentation:', error);
      showToast('Failed to delete presentation');
    }
  };

  const toggleFeatured = async (presentation) => {
    try {
      const result = await databaseService.updateDocument(
        PRESENTATIONS_COLLECTION,
        presentation.$id,
        { featured: !presentation.featured }
      );
      if (result.success) {
        showToast(`Presentation ${!presentation.featured ? 'featured' : 'hidden'}`);
        loadPresentations();
      }
    } catch (error) {
      showToast('Failed to update');
    }
  };

  const movePresentation = async (index, direction) => {
    const newPresentations = [...presentations];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newPresentations.length) return;

    [newPresentations[index], newPresentations[targetIndex]] = [newPresentations[targetIndex], newPresentations[index]];

    try {
      await Promise.all([
        databaseService.updateDocument(PRESENTATIONS_COLLECTION, newPresentations[index].$id, { order: index }),
        databaseService.updateDocument(PRESENTATIONS_COLLECTION, newPresentations[targetIndex].$id, { order: targetIndex })
      ]);
      loadPresentations();
    } catch (error) {
      showToast('Failed to reorder');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getSlideCount = (presentation) => {
    try {
      return JSON.parse(presentation.slideImages || '[]').length;
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#105652]">Presentations</h1>
          <p className="text-gray-500 mt-1">Upload slide images for your presentations.</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#105652] text-white rounded-lg hover:opacity-90 transition-all shadow-md hover:shadow-lg active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Add Presentation
        </button>
      </div>

      {/* Presentations Grid */}
      {presentations.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Presentation className="w-10 h-10 text-[#105652]" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No presentations yet</h3>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">Start by uploading your first presentation slides to showcase your work.</p>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#105652] text-white rounded-xl hover:opacity-90 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Add Presentation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {presentations.map((presentation, index) => (
            <div
              key={presentation.$id}
              className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
            >
              {/* Card Header (Preview) */}
              <div className="relative h-48 bg-gradient-to-br from-[#105652] to-[#1E8479] overflow-hidden">
                {presentation.thumbnailUrl ? (
                  <>
                    <img
                      src={presentation.thumbnailUrl}
                      alt={presentation.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Presentation className="w-16 h-16 text-white/30" />
                  </div>
                )}
                
                {/* Status Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                   {presentation.featured && (
                     <span className="bg-green-500/90 backdrop-blur text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1">
                       <Eye className="w-3 h-3" /> Featured
                     </span>
                   )}
                   {presentation.category && (
                     <span className="bg-white/90 backdrop-blur text-[#105652] text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                       {presentation.category}
                     </span>
                   )}
                </div>

                {/* Slide Count Badge */}
                <div className="absolute bottom-4 right-4">
                  <span className="bg-black/50 backdrop-blur text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5">
                    <Layers className="w-3 h-3" />
                    {getSlideCount(presentation)} slides
                  </span>
                </div>
                
                 {/* Quick Actions Overlay */}
                 <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0 duration-300">
                    <button 
                      onClick={() => movePresentation(index, 'up')} 
                      disabled={index === 0}
                      className="p-1.5 bg-white/90 backdrop-blur text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                    >
                       <ChevronUp className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => movePresentation(index, 'down')} 
                      disabled={index === presentations.length - 1}
                      className="p-1.5 bg-white/90 backdrop-blur text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                    >
                       <ChevronDown className="w-4 h-4" />
                    </button>
                 </div>
              </div>

              {/* Card Content */}
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 line-clamp-1 group-hover:text-[#105652] transition-colors mb-2">
                  {presentation.title}
                </h3>
                
                {presentation.description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">{presentation.description}</p>
                )}
                
                <div className="space-y-2 mb-6 mt-auto">
                  {presentation.author && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{presentation.author}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{formatDate(presentation.presentationDate)}</span>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-4 border-t border-gray-100 grid grid-cols-5 gap-1.5">
                  <button
                    onClick={() => toggleFeatured(presentation)}
                    className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                      presentation.featured 
                        ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {presentation.featured ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {presentation.featured ? 'Show' : 'Hide'}
                  </button>
                  <button
                    onClick={() => openModal(presentation)}
                    className="flex items-center justify-center gap-1 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-medium transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  {presentation.slug ? (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/presentation?pres=${presentation.slug}`);
                        setCopiedCardId(presentation.$id);
                        showToast('Link copied!');
                        setTimeout(() => setCopiedCardId(null), 2000);
                      }}
                      className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                        copiedCardId === presentation.$id
                          ? 'bg-green-500 text-white'
                          : 'bg-[#105652]/10 text-[#105652] hover:bg-[#105652]/20'
                      }`}
                    >
                      {copiedCardId === presentation.$id ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                      {copiedCardId === presentation.$id ? 'Copied' : 'Link'}
                    </button>
                  ) : (
                    <button
                      onClick={() => openModal(presentation)}
                      className="flex items-center justify-center gap-1 py-2 rounded-lg bg-gray-100 text-gray-400 text-xs font-medium cursor-not-allowed"
                      title="Add a slug to enable sharing"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      No Slug
                    </button>
                  )}
                  <Link
                    to={`/admin/analytics/presentations/${presentation.$id}`}
                    className="flex items-center justify-center gap-1 py-2 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 text-xs font-medium transition-colors"
                  >
                    <BarChart2 className="w-3.5 h-3.5" />
                    Stats
                  </Link>
                  <button
                    onClick={() => handleDelete(presentation)}
                    className="flex items-center justify-center gap-1 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-xs font-medium transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                 <h2 className="text-2xl font-bold text-gray-900">
                  {editingPresentation ? 'Edit Presentation' : 'Add New Presentation'}
                </h2>
                <p className="text-sm text-gray-500">Upload slide images for your presentation</p>
              </div>
              <button 
                onClick={closeModal} 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
              <div className="space-y-6">
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all"
                    placeholder="e.g., Climate Change Analysis 2024"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">Short Description</label>
                    <span className="text-xs text-gray-400">85 words max</span>
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all resize-none"
                    placeholder="Brief description of the presentation..."
                    rows={3}
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-gray-400">Keep it concise for best display on cards</span>
                    <span className={`text-xs font-medium ${
                      formData.description.trim().split(/\s+/).filter(w => w).length > 85 
                        ? 'text-red-500' 
                        : formData.description.trim().split(/\s+/).filter(w => w).length > 70 
                          ? 'text-amber-500' 
                          : 'text-[#105652]'
                    }`}>
                      {formData.description.trim() ? formData.description.trim().split(/\s+/).filter(w => w).length : 0} / 85 words
                    </span>
                  </div>
                </div>

                {/* Category & Author */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all"
                      placeholder="e.g., Research, Lecture"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Author/Presenter</label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all"
                      placeholder="Presenter name"
                    />
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Presentation Date</label>
                  <input
                    type="date"
                    value={formData.presentationDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, presentationDate: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all"
                  />
                </div>

                {/* Slug for Shareable Link */}
                <div className="bg-gradient-to-r from-[#105652]/5 to-[#1E8479]/5 rounded-xl p-4 border border-[#105652]/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Link2 className="w-4 h-4 text-[#105652]" />
                    <label className="text-sm font-medium text-gray-700">Shareable Link (Slug)</label>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') }))}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#105652]/20 focus:border-[#105652] outline-none transition-all text-sm"
                      placeholder="e.g., research-proposal-2024"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, slug: generateSlug(prev.title || '') }))}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                      title="Generate from title"
                    >
                      Auto Generate
                    </button>
                  </div>
                  {formData.slug && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-gray-200 text-xs text-gray-600 font-mono overflow-hidden">
                        <span className="text-gray-400">{window.location.origin}/presentation?pres=</span>
                        <span className="text-[#105652] font-semibold">{formData.slug}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/presentation?pres=${formData.slug}`);
                          setLinkCopied(true);
                          showToast('Shareable link copied!');
                          setTimeout(() => setLinkCopied(false), 2000);
                        }}
                        className={`p-2.5 rounded-lg transition-all ${linkCopied ? 'bg-green-500 text-white' : 'bg-[#105652] text-white hover:bg-[#0d4541]'}`}
                        title="Copy shareable link"
                      >
                        {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    This creates a direct link that scrolls to and highlights this presentation
                  </p>
                </div>


                {/* Slide Images Upload */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">Slide Images *</label>
                    <span className="text-xs text-gray-500">{slideImages.length} slides uploaded</span>
                  </div>
                  
                  {/* Uploaded Slides Grid */}
                  {slideImages.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      {slideImages.map((slide, index) => (
                        <div key={index} className="relative group aspect-[16/9] rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                          <img 
                            src={slide.url} 
                            alt={`Slide ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {/* Slide number */}
                          <div className="absolute top-1 left-1 w-5 h-5 rounded bg-black/70 text-white text-xs flex items-center justify-center font-medium">
                            {index + 1}
                          </div>
                          {/* Actions overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => moveSlide(index, 'up')}
                              disabled={index === 0}
                              className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white disabled:opacity-30"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSlide(index, 'down')}
                              disabled={index === slideImages.length - 1}
                              className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white disabled:opacity-30"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeSlide(index)}
                              className="p-1 bg-red-500 rounded text-white hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  <label className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadingSlides ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-[#105652] hover:bg-teal-50/30'}`}>
                    {uploadingSlides ? (
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#105652]"></div>
                        <span className="text-sm text-gray-500">Uploading slides...</span>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-10 h-10 text-gray-400 mb-3" />
                        <p className="text-sm font-medium text-gray-600">Click to upload slide images</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP (Select multiple)</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleSlidesUpload}
                      className="hidden"
                      disabled={uploadingSlides}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <Layers className="w-3 h-3" />
                    Tip: Export slides from PowerPoint as images (File → Export → Images)
                  </p>
                </div>

                {/* Thumbnail Upload (Optional - will use first slide if not set) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Cover Image (Optional)</label>
                  <p className="text-xs text-gray-500 mb-2">Uses first slide if not set</p>
                  {formData.thumbnailUrl ? (
                    <div className="relative">
                      <img 
                        src={formData.thumbnailUrl} 
                        alt="Thumbnail" 
                        className="w-full h-40 object-cover rounded-xl border border-gray-200"
                      />
                      <label className="absolute bottom-3 right-3 px-3 py-1.5 bg-white/90 backdrop-blur text-gray-700 text-sm rounded-lg cursor-pointer hover:bg-white transition-colors shadow-md">
                        Change
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          className="hidden"
                          disabled={uploadingThumbnail}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadingThumbnail ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-[#105652] hover:bg-teal-50/30'}`}>
                      {uploadingThumbnail ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#105652]"></div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Upload custom cover image</p>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                        disabled={uploadingThumbnail}
                      />
                    </label>
                  )}
                </div>

                {/* Featured Checkbox */}
                 <div className="flex items-center gap-3 p-4 bg-teal-50 rounded-xl border border-teal-100">
                    <input
                      type="checkbox"
                      id="featured-modal"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-5 h-5 rounded text-[#105652] focus:ring-[#105652]"
                    />
                    <div>
                      <label htmlFor="featured-modal" className="block text-sm font-semibold text-gray-900">Featured Presentation</label>
                      <p className="text-xs text-gray-500">Show on the presentation gallery page</p>
                    </div>
                 </div>

              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
                <button type="button" onClick={closeModal} className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || uploadingSlides || uploadingThumbnail}
                  className="inline-flex items-center gap-2 px-8 py-2.5 bg-[#105652] text-white rounded-xl hover:opacity-90 font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : (editingPresentation ? 'Update Presentation' : 'Add Presentation')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
